import { Controller } from 'stimulus';
import Swiper, { Autoplay, EffectFade, Pagination } from 'swiper';
Swiper.use([Autoplay, EffectFade, Pagination]);

export default class extends Controller {
  static targets = ['slider', 'pagination'];
  swiper = null;
  activeFullVideoSlide = null;

  connect() {
    this.initHeroSlider();
    this.initIntersectionObserver();
  }

  initHeroSlider() {
    this.swiper = new Swiper(this.sliderTarget, {
      slidesPerView: 1,
      loop: true,
      speed: 1000,
      effect: 'fade',
      autoHeight: true,
      fadeEffect: {
        crossFade: true,
      },
      autoplay: {
        delay: 3000,
        disableOnInteraction: true,
      },
      pagination: {
        el: this.paginationTarget,
        clickable: true,
      },
    });

    this.swiper.on('slideChange', () => this.resetSlides());
    this.swiper.on('slideChangeTransitionEnd', () => this.revealControls());
  }

  togglePlay(event) {
    const button = event.currentTarget;
    const video = this.previewVideoFor(button);
    if (!video) return;

    if (video.paused) {
      video.play();
      button.classList.remove('is-paused');
      button.setAttribute('aria-label', 'Pause video');
    } else {
      video.pause();
      button.classList.add('is-paused');
      button.setAttribute('aria-label', 'Play video');
    }
  }

  watchFull(event) {
    const slide = event.currentTarget.closest('.js-heroSlide');
    if (!slide) return;

    // Keep the slider from advancing while the full video plays.
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop();
    }

    const preview = slide.querySelector('.js-heroPreviewVideo');
    const fullVideo = slide.querySelector('.js-heroFullVideo');
    const playToggle = slide.querySelector('.js-heroPlayToggle');
    const watchButton = slide.querySelector('.js-heroWatchFull');
    if (!fullVideo) return;

    if (preview) {
      preview.pause();
      preview.hidden = true;
    }
    if (playToggle) playToggle.hidden = true;
    if (watchButton) watchButton.hidden = true;

    // Remember this slide so its controls stay hidden through the next
    // transition and only reappear once that transition finishes.
    this.activeFullVideoSlide = slide;

    fullVideo.hidden = false;
    fullVideo.currentTime = 0;
    fullVideo.play();
  }

  resetSlides() {
    this.element
      .querySelectorAll('.js-heroSlide')
      .forEach((slide) => this.restorePreview(slide));
  }

  restorePreview(slide) {
    const preview = slide.querySelector('.js-heroPreviewVideo');
    const fullVideo = slide.querySelector('.js-heroFullVideo');
    const playToggle = slide.querySelector('.js-heroPlayToggle');
    const watchButton = slide.querySelector('.js-heroWatchFull');

    if (fullVideo) {
      fullVideo.pause();
      fullVideo.currentTime = 0;
      fullVideo.hidden = true;
    }
    if (preview) {
      preview.hidden = false;
      preview.play().catch(() => {});
    }

    // Only the slide we just left full-video mode on keeps its controls
    // hidden during the transition (revealControls shows them at the end).
    // Every other slide keeps its controls visible so they don't flash in.
    const deferControls = slide === this.activeFullVideoSlide;
    if (playToggle) {
      playToggle.hidden = deferControls;
      playToggle.classList.remove('is-paused');
      playToggle.setAttribute('aria-label', 'Pause video');
    }
    if (watchButton) watchButton.hidden = deferControls;
  }

  revealControls() {
    if (!this.activeFullVideoSlide) return;

    const playToggle = this.activeFullVideoSlide.querySelector('.js-heroPlayToggle');
    const watchButton = this.activeFullVideoSlide.querySelector('.js-heroWatchFull');
    if (playToggle) playToggle.hidden = false;
    if (watchButton) watchButton.hidden = false;

    this.activeFullVideoSlide = null;
  }

  previewVideoFor(button) {
    const slide = button.closest('.js-heroSlide');
    return slide ? slide.querySelector('.js-heroPreviewVideo') : null;
  }

  initIntersectionObserver() {
    const options = {
      root: null,
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.swiper.autoplay.start();
        } else {
          this.swiper.autoplay.stop();
        }
      });
    }, options);

    observer.observe(this.sliderTarget);
  }
}
